import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { BlogArticle } from "@/components/site/BlogArticle";
import { articleJsonLd, blogBreadcrumbJsonLd } from "@/content/blog-types";
import { faqJsonLd } from "@/content/faqs";
import { canonical, SITE_URL, shareImageMetaFromPath, langPageMeta } from "@/content/site";
import { publishedPostQuery } from "@/lib/blog-queries";
import { heroDelivery, heroPreloadLinks } from "@/lib/hero-delivery";

export const Route = createFileRoute("/$lang/blog/$slug")({
  staticData: { sitemap: false },
  loader: async ({ context, params }) => {
    const result = await context.queryClient.ensureQueryData(publishedPostQuery(params.slug));
    if (!result) throw notFound();
    return result;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Artikel niet gevonden" }, { name: "robots", content: "noindex" }] };
    }
    const { post } = loaderData;
    const path = `/blog/${post.slug}`;
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.metaDescription },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.metaDescription },
        { property: "og:type", content: "article" },
        ...langPageMeta(params.lang, path).meta,
        { name: "twitter:card", content: "summary_large_image" },
        ...shareImageMetaFromPath(post.ogImage ?? post.image, post.imageAlt),
      ],
      links: [...langPageMeta(params.lang, path).links, ...heroPreloadLinks(heroDelivery(null, { src: post.image, variants: post.imageVariants }))],
      scripts: [articleJsonLd(post, SITE_URL), blogBreadcrumbJsonLd(post, SITE_URL), faqJsonLd(post.faq)],
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(publishedPostQuery(slug));
  if (!data) return null;
  return <BlogArticle post={data.post} related={data.related} />;
}
